import React from "react";
import { Link } from "react-router-dom";
import { Controller } from "react-hook-form";
import { Col, Form, FormControl } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { FaEye, FaEyeSlash, FaRegCalendarAlt, FaMinus } from "react-icons/fa";
import { buildValidationRules } from "./registerDataHelpers";
import PropTypes from "prop-types";
import { getSubdomainPath, normalizeMonthYear } from "../../../utils/helpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const renderLinkedInField = (field, deps) => {
  const { control, errors } = deps;
  const { placeholder, validation, is_required, status } = field;
  const validationRule = validation;
  const subDomainPath = getSubdomainPath();
  return status ? (
    <Col md={12} className="mb-3">
      <div
        className="form-group position-relative"
        style={{ marginBottom: "0px" }}
      >
        <Form.Label>{field.label}</Form.Label>
        <div className="form-label overflow-hidden ">
          <span>
            <i className="fab fa-linkedin-in"></i>
          </span>

          <Controller
            name="linkedin"
            control={control}
            defaultValue=""
            rules={{
              ...buildValidationRules(validationRule, is_required),
            }}
            render={({ field }) => (
              <Form.Control
                type="text"
                placeholder={placeholder}
                className="form-control"
                {...field}
              />
            )}
          />
        </div>
        {(subDomainPath !== 'siddev' && subDomainPath !== 'siduat' && subDomainPath !== 'sid') &&
          `If you are registering as both a mentor and a mentee, please add a
          single or double slash ("/" or "//") at the end of your LinkedIn link.`
        }
      </div>

      {errors?.linkedin && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors.linkedin.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderProgramStudyOtherField = (field, deps) => {
  const { register, errors, watch } = deps;
  const { reg_key, field_type, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const isNameField = ["first_name", "last_name"].includes(reg_key);

  let columnClass = "";
  if (reg_key === "first_name") {
    columnClass = "pe-sm-2";
  } else if (reg_key === "last_name") {
    columnClass = "ps-sm-2";
  }

  return status && watch("program_study_id") === "0" ? (
    <Col
      md={isNameField ? 6 : 12}
      sm={isNameField ? 6 : 12}
      className={`mb-3 ${columnClass}`}
    >
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicEmail">
        <Form.Control
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            validate: {
              whiteSpace: (value) =>
                !value ||
                  (typeof value === "string" && value.trim().length > 0)
                  ? true
                  : "White spaces not allowed.",
            },
          })}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderGenericTextField = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, field_type, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const isNameField = ["first_name", "last_name"].includes(reg_key);

  let columnClass = "";
  if (reg_key === "first_name") {
    columnClass = "pe-sm-2";
  } else if (reg_key === "last_name") {
    columnClass = "ps-sm-2";
  }

  return status ? (
    <Col
      md={isNameField ? 6 : 12}
      sm={isNameField ? 6 : 12}
      className={`mb-3 ${columnClass}`}
    >
      <Form.Label>{field.label}</Form.Label>
      {(reg_key === "restrictions_boundaries" ||
        reg_key === "communication_tools" ||
        reg_key === "frequency_communication" ||
        reg_key === "topics") && (
          // <Form.Label>{field.label}</Form.Label>
          <></>
        )}

      <Form.Control
        type={field_type}
        placeholder={placeholder}
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
          validate: {
            whiteSpace: (value) =>
              !value ||
                (typeof value === "string" && value.trim().length > 0)
                ? true
                : "White spaces not allowed.",
          },
        })}
      />
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

export const renderTextField = (field, deps) => {
  const { reg_key } = field;

  if (reg_key === "linkedin") {
    return renderLinkedInField(field, deps);
  }

  if (reg_key === "program_study_other") {
    return renderProgramStudyOtherField(field, deps);
  }

  if (reg_key === "past_board_roles") {
    return renderPastBoardRolesField(field, deps);
  }

  // Handle board_roles repeater field (dynamic groups of company/board info)
  if (reg_key === "board_roles") {
    return renderBoardRolesRepeaterField(field, deps);
  }

  return renderGenericTextField(field, deps);
};

export const renderEmailField = (field, deps) => {
  const { register, errors, email, setEmail, readOnly, setValue } = deps;
  const { reg_key, field_type, placeholder, validation, is_required } = field;
  const validationRule = validation;

  return (
    <Col md={12} sm={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicEmail">
        <FormControl
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          value={email}
          readOnly={readOnly}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          onChange={(e) => {
            setValue("email", e.target.value);
            setEmail(e?.target?.value);
          }}
        />
        {errors?.email && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.email?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

export const renderPasswordField = (field, deps) => {
  const { register, errors, showPassword, togglePasswordVisibility } = deps;
  const { reg_key, placeholder, validation, is_required } = field;
  const validationRule = validation;

  return (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicPassword">
        <div style={{ position: "relative" }}>
          <FormControl
            type={showPassword ? "text" : "password"}
            placeholder={placeholder}
            style={{ paddingRight: "43px" }}
            name={reg_key}
            {...register(reg_key, {
              ...buildValidationRules(validationRule, is_required),
            })}
          />
          <button
            type="button"
            className="password-toggle-icon"
            onClick={togglePasswordVisibility}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                togglePasswordVisibility();
              }
            }}
            style={{
              cursor: "pointer",
              position: "absolute",
              right: "13px",
              top: "47%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </button>
        </div>
        {errors?.password && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.password?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

const renderRoleSelect = (field, deps) => {
  const { register, errors, handleChange, roleId, emailVerifyString } = deps;
  const { reg_key, placeholder, validation, is_required } = field;
  const validationRule = validation;

  return (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <select
        name={reg_key}
        disabled={!!emailVerifyString}
        className={"form-control form_react_Select"}
        value={roleId}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
      >
        <option value="">{placeholder}</option>
        <option value="Mentor">Mentor</option>
        <option value="Mentees">Mentee</option>
      </select>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  );
};

const renderProgramSelect = (field, deps) => {
  const { control, programmes, setValue } = deps;
  const { reg_key, placeholder, status } = field;
  const defaultProgramme = programmes?.[0]?.id ?? "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Controller
        name={reg_key}
        control={control}
        rules={{}}
        defaultValue={defaultProgramme}
        render={({ field: controllerField }) => {
          const currentValue = controllerField.value !== undefined && controllerField.value !== ""
            ? controllerField.value
            : defaultProgramme;

          // Sync into RHF state so the value appears in the submit payload.
          // This is needed because disabled fields are excluded from native form
          // submission, and Controller's defaultValue alone is not enough when
          // superUserParams doesn't pre-populate this key.
          // Use both conditions to ensure proper sync when programmes data loads
          if (programmes && programmes.length > 0 && (!controllerField.value || controllerField.value === "")) {
            // Immediately sync the default value into form state
            setValue(reg_key, defaultProgramme, { shouldValidate: false });
          } else if (currentValue !== "" && controllerField.value !== currentValue) {
            // Also handle case where value needs updating
            setTimeout(() => setValue(reg_key, currentValue, { shouldValidate: false }), 0);
          }

          return (
            <select
              value={currentValue}
              disabled
              className="form-control"
            >
              <option value="">{placeholder}</option>
              {programmes?.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.program}
                </option>
              ))}
            </select>
          );
        }}
      />
    </Col>
  ) : null;
};


const renderProgramStudySelect = (field, deps) => {
  const { register, errors, setValue, trigger, clearErrors, programStudyId, handleProgrammesChange } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <select
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
        onChange={(e) => {
          const value = e.target.value;
          handleProgrammesChange(value);
          setValue(reg_key, value);
          if (value) {
            clearErrors(reg_key);
          } else {
            trigger(reg_key);
          }
        }}
      >
        <option value="">{placeholder}</option>
        {programStudyId?.map((value) => (
          <option key={value.id} value={value.id}>
            {value.title}
          </option>
        ))}
        <option value="0">Others</option>
      </select>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
            display: "block",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderUniversityPartnerSelect = (field, deps) => {
  const { register, errors, trigger, clearErrors, university_partners_list } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicPassword">
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          className={"form-control"}
          onChange={(e) => {
            clearErrors(reg_key);
            if (!e.target.value && is_required) {
              trigger(reg_key);
            }
          }}
        >
          <option value="">{placeholder}</option>
          {university_partners_list?.map((item) => (
            <option value={item.id} key={item.id}>
              {item.title}
            </option>
          ))}
        </Form.Control>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderYearSelect = (field, deps) => {
  const { register, errors, years } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formCurrentCourse">
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          className={"form-control"}
        >
          <option value="">{placeholder}</option>
          {years?.map((year) => (
            <option value={year} key={year}>
              {year}
            </option>
          ))}
        </Form.Control>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderExperienceSelect = (field, deps) => {
  const { register, errors, setValue, trigger, userExperience, setExperience } = deps;
  const { reg_key, placeholder, validation, is_required, status } = field;
  const validationRule = validation;

  return status ? (
    <Col md="12" className="mb-3">
      <Form.Group controlId="formBasicPassword">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            onChange: async (e) => {
              const value = e.target.value;
              setExperience([value]);
              setValue(reg_key, value, { shouldValidate: true });
              await trigger(reg_key);
            },
          })}
        >
          <option value="">{placeholder}</option>
          {userExperience?.map((val) => (
            <option key={val?.id} value={val?.id}>
              {val?.title}
            </option>
          ))}
        </Form.Control>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderCountrySelect = (field, deps) => {
  const { register, errors, trigger, clearErrors, country, country1, userData, setValue, setCountry1, watch } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const currentCountry = watch(reg_key) || country1 || userData.country || "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        value={currentCountry}
        className="mb-1"
        name={reg_key}
        onChange={(e) => {
          const selectedCountry = e.target.value;
          setValue(reg_key, selectedCountry);
          if (setCountry1) {
            setCountry1(selectedCountry);
          }
          clearErrors(reg_key);
          if (!selectedCountry && is_required) {
            trigger(reg_key);
          }
        }}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
      >
        <option value="">{placeholder}</option>
        {country?.map((item) => (
          <option value={item.label} key={item.label}>
            {item.label}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderLanguageSelect = (field, deps) => {
  const { control, errors, setValue, languageOptions, customStyles } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId="formBasicLanguage">
        <Form.Label>{label}</Form.Label>
        <Controller
          name="language"
          control={control}
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => (
            <Select
              {...field}
              isMulti
              options={languageOptions}
              placeholder={placeholder}
              className="basic-multi-select language_select"
              classNamePrefix="select"
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions?.map((opt) => opt.value)
                  : [];
                field.onChange(selectedValues);
                setValue(
                  "language",
                  selectedValues?.length > 0 ? selectedValues : ""
                );
              }}
              value={languageOptions?.filter((option) =>
                field.value?.includes(option?.value)
              )}
              styles={customStyles}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderIndustrySelect = (field, deps) => {
  const { register, errors, setIndustryData, industryData, industry_list } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        defaultValue={industryData}
        className="mb-1"
        name={reg_key}
        onChange={(e) => {
          setIndustryData(e?.target?.value);
        }}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
      >
        <option value="">{placeholder}</option>
        {industry_list?.map((item) => (
          <option value={item.id} key={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderMenteeCountSelect = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className={"form-control"}
      >
        <option value="">{placeholder}</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
        <option value="5">5</option>
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderProgramInterestSelect = (field, deps) => {
  const { register, errors, particularProgram } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md="12" className=" mb-3">
      <Form.Label>{field.label}</Form.Label>

      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className={"form-control"}
      >
        <option value="">{placeholder}</option>
        {particularProgram?.map((res, i) => {
          return (
            <option key={res?.id} value={res?.id}>
              {`${res?.title}`}
            </option>
          );
        })}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

// Board-related field renderers
const renderBoardEntityTypeSelect = (field, deps) => {
  const { control, errors, setValue, boardEntityTypeExperience, boardExperienceMentor, customStyles } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  // Use different data source based on field type
  const dataSource = reg_key === "board_entity_type_2" ? boardExperienceMentor : boardEntityTypeExperience;
  const options = dataSource?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(
                    reg_key,
                    selectedValues?.length > 0 ? selectedValues : ""
                  );
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderYearsOfBoardExperienceSelect = (field, deps) => {
  const { register, errors, boardExperience } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className="form-control"
      >
        <option value="">{placeholder}</option>
        {boardExperience?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderAccreditedDirectorSelect = (field, deps) => {
  const { register, errors, accreditedDirector } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className="form-control"
      >
        <option value="">{placeholder}</option>
        {accreditedDirector?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderBoardExperienceLevelSelect = (field, deps) => {
  const { control, errors, boardExperienceLevel, customStyles, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = boardExperienceLevel?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  const requiredMessage = validationRule?.find(v => v.key === "required")?.message || `${field.label} is required`;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
            ...(is_required === 1 && {
              validate: (value) => {
                const arr = Array.isArray(value) ? value : [];
                return arr.length > 0 || requiredMessage;
              }
            }),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(
                    reg_key,
                    selectedValues?.length > 0 ? selectedValues : ""
                  );
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderCoursesCompletedSelect = (field, deps) => {
  const { register, errors, coursesCompleted } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className="form-control"
      >
        <option value="">{placeholder}</option>
        {coursesCompleted?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderCoursesCompletedCustomSelect = (field, deps) => {
  const { control, errors, coursesCompleted, customStyles, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = coursesCompleted?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(reg_key, selectedValues);
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};
const renderAccreditedDirectorCustomSelect = (field, deps) => {
  const { control, errors, accreditedDirector, customStyles, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = accreditedDirector?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(reg_key, selectedValues);
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

// Repeater field for board roles - handles dynamic groups of company/board info
// Each group contains: rep_company_name, rep_board_title, rep_start_date, rep_end_date
// Min/max constraints come from board_roles_validation in backend settings (parsed as JSON)
const renderBoardRolesRepeaterField = (field, deps) => {
  const { roleId, setting } = deps;
  const { watch, setValue, clearErrors, trigger, errors } = deps;
  const { reg_key, status } = field;

  // Parse board_roles_validation from settings
  // It's a JSON string with min/max per role type
  // Example: [{"key":"min","type":"Mentor","value":3},{"key":"max","type":"Mentees","value":3}]
  let minRoles = 0;
  let maxRoles = 1;

  if (setting?.board_roles_validation) {
    try {
      // Parse JSON string if needed
      const validationData = typeof setting.board_roles_validation === 'string'
        ? JSON.parse(setting.board_roles_validation)
        : setting.board_roles_validation;

      // Find min and max for current role type (Mentor or Mentees)
      if (Array.isArray(validationData)) {
        const minObj = validationData.find(v => v.key === 'min' && v.type === roleId);
        const maxObj = validationData.find(v => v.key === 'max' && v.type === roleId);

        minRoles = minObj?.value || 0;
        maxRoles = maxObj?.value || 1;
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  // Watch the board_roles array to track current count
  const boardRolesArray = watch(reg_key) || [];

  // Check if we should show the repeater (min > 0 means it's required)
  const shouldShowRepeater = minRoles > 0;

  // Check if user can add more roles (current count < max)
  const canAddMore = boardRolesArray.length < maxRoles;

  // Add a new empty board role group
  const handleAddRole = () => {
    if (canAddMore) {
      const newRole = {
        rep_company_name: "",
        rep_board_title: "",
        rep_start_date: "",
        rep_end_date: "",
      };
      const updatedRoles = [...boardRolesArray, newRole];
      setValue(reg_key, updatedRoles);
      clearErrors(reg_key);
    }
  };

  // Remove a specific board role group by index
  const handleRemoveRole = (index) => {
    const updatedRoles = boardRolesArray.filter((_, i) => i !== index);
    setValue(reg_key, updatedRoles);
    // Re-trigger validation after removal
    trigger(reg_key);
  };

  // Update a specific field within a board role group
  const handleFieldChange = (index, fieldName, value) => {
    const updatedRoles = [...boardRolesArray];
    updatedRoles[index] = {
      ...updatedRoles[index],
      [fieldName]: value,
    };
    setValue(reg_key, updatedRoles);
    clearErrors(reg_key);
  };

  // Validate date range: rep_start_date must be <= rep_end_date
  // Start Date is required (Yes), End Date is optional (No)
  // Validation rules:
  // - If both dates provided: start_date must be <= end_date
  // - If only end_date provided without start_date: error (start is required)
  // - If only start_date provided: valid (end is optional)
  const validateDateRange = (index) => {
    const role = boardRolesArray[index];

    // Only validate if both dates are provided
    // If end date exists, start date must also exist and be <= end date
    if (role?.rep_start_date && role?.rep_end_date) {
      const startDate = new Date(role.rep_start_date);
      const endDate = new Date(role.rep_end_date);

      // Check if start date is after end date (invalid)
      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }

    // If only end date is provided without start date, that's an error
    // because start date is required
    if (!role?.rep_start_date && role?.rep_end_date) {
      return "Start date is required when end date is provided";
    }

    return null;
  };

  return shouldShowRepeater && status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        {/* Render each board role group */}
        {boardRolesArray.map((role, index) => (
          <div
            key={role}
            className="board-role-group mb-4 p-3"
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              backgroundColor: "#f9f9f9",
            }}
          >
            {/* Group header with remove button */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Board Role #{index + 1}</h6>
              {boardRolesArray.length > minRoles && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger d-flex align-items-center"
                  onClick={() => handleRemoveRole(index)}
                  title="Remove this board role"
                >
                  <FaMinus size={10} className="me-1" /> Remove
                </button>
              )}
            </div>

            {/* Company Name Field */}
            <div className="mb-3">
              <Form.Label className="form-label">Company Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter company name"
                maxLength={255}
                value={role?.rep_company_name || ""}
                onChange={(e) =>
                  handleFieldChange(index, "rep_company_name", e.target.value)
                }
                className={
                  errors?.[`${reg_key}_${index}_company`]
                    ? "is-invalid"
                    : ""
                }
              />
              {errors?.[`${reg_key}_${index}_company`] && (
                <span
                  role="alert"
                  style={{
                    color: "#dc3545",
                    fontSize: "0.875em",
                    margin: "4px 0px 8px",
                    display: "block",
                  }}
                >
                  {errors?.[`${reg_key}_${index}_company`]?.message}
                </span>
              )}
            </div>

            {/* Board Title Field */}
            <div className="mb-3">
              <Form.Label className="form-label">Board Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter board title"
                maxLength={255}
                value={role?.rep_board_title || ""}
                onChange={(e) =>
                  handleFieldChange(index, "rep_board_title", e.target.value)
                }
                className={
                  errors?.[`${reg_key}_${index}_title`] ? "is-invalid" : ""
                }
              />
              {errors?.[`${reg_key}_${index}_title`] && (
                <span
                  role="alert"
                  style={{
                    color: "#dc3545",
                    fontSize: "0.875em",
                    margin: "4px 0px 8px",
                    display: "block",
                  }}
                >
                  {errors?.[`${reg_key}_${index}_title`]?.message}
                </span>
              )}
            </div>

            {/* Start Date and End Date Fields (side by side) */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label className="form-label">Start Date *</Form.Label>
                <Form.Control
                  type="date"
                  autoComplete="off"
                  value={role?.rep_start_date || ""}
                  onChange={(e) =>
                    handleFieldChange(index, "rep_start_date", e.target.value)
                  }
                  className={
                    errors?.[`${reg_key}_${index}_startDate`]
                      ? "is-invalid"
                      : ""
                  }
                />
                {errors?.[`${reg_key}_${index}_startDate`] && (
                  <span
                    role="alert"
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875em",
                      margin: "4px 0px 8px",
                      display: "block",
                    }}
                  >
                    {errors?.[`${reg_key}_${index}_startDate`]?.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label className="form-label">End Date</Form.Label>
                <Form.Control
                  type="date"
                  autoComplete="off"
                  value={role?.rep_end_date || ""}
                  onChange={(e) =>
                    handleFieldChange(index, "rep_end_date", e.target.value)
                  }
                  className={
                    errors?.[`${reg_key}_${index}_endDate`] ? "is-invalid" : ""
                  }
                />
                {errors?.[`${reg_key}_${index}_endDate`] && (
                  <span
                    role="alert"
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875em",
                      margin: "4px 0px 8px",
                      display: "block",
                    }}
                  >
                    {errors?.[`${reg_key}_${index}_endDate`]?.message}
                  </span>
                )}
              </div>
            </div>

            {/* Date range validation error */}
            {validateDateRange(index) && (
              <div
                style={{
                  color: "#dc3545",
                  fontSize: "0.875em",
                  marginBottom: "8px",
                }}
              >
                {validateDateRange(index)}
              </div>
            )}
          </div>
        ))}

        {/* Add More Button - shown only if max not reached */}
        {canAddMore && (
          <button
            type="button"
            className="btn loginsubmit btn-red mt-2"
            onClick={handleAddRole}
            title={`Add another board role (${boardRolesArray.length}/${maxRoles})`}
          >
            + Add Board Role
          </button>
        )}

        {/* Display current count */}
        <div className="mt-2" style={{ fontSize: "0.875em", color: "#6c757d" }}>
          {boardRolesArray.length} of {maxRoles} board roles added
        </div>

        {/* Overall validation error */}
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
              display: "block",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderPastBoardRolesField = (field, deps) => {
  const { control, errors, setting } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  let maxRoles = 6;

  if (setting?.board_roles_validation) {
    try {
      // Parse JSON string if needed
      const validationData = typeof setting.board_roles_validation === 'string'
        ? JSON.parse(setting.board_roles_validation)
        : setting.board_roles_validation;

      // Find min and max values - ignore the type field completely
      if (Array.isArray(validationData)) {
        const maxObj = validationData.find(v => v.key === 'max');
        if (maxObj?.value) {
          maxRoles = maxObj.value;
        }
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  // Prioritize past_board_roles_count from settings if available
  if (
    setting?.past_board_roles_count !== undefined &&
    setting?.past_board_roles_count !== null &&
    setting?.past_board_roles_count !== ""
  ) {
    const parsedCount = parseInt(setting.past_board_roles_count, 10);
    if (!isNaN(parsedCount) && parsedCount > 0) {
      maxRoles = parsedCount;
    }
  } else if (maxRoles < 6) {
    maxRoles = 6;
  }

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),

            required: `Current / Past Board Roles minimum 1 and maximum ${maxRoles} roles are required.`,

            validate: (value) => {
              if (!value || value.length === 0) {
                return `Current / Past Board Roles minimum 1 and maximum ${maxRoles} roles are required.`;
              }

              if (value.length > maxRoles) {
                return `Current / Past Board Roles minimum 1 and maximum ${maxRoles} roles are required.`;
              }

              return true;
            }
          }}
          render={({ field }) => (
            <CreatableSelect
              {...field}
              isMulti
              options={[]}
              placeholder={placeholder}
              className="basic-multi-select"
              classNamePrefix="select"
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions.map((opt) => opt.value || opt.label)
                  : [];
                field.onChange(selectedValues);
              }}
              value={
                Array.isArray(field.value)
                  ? field.value.map(val => ({ value: val, label: val }))
                  : []
              }
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                Menu: () => null, // Remove the dropdown menu completely
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  border: '1px solid #ced4da',
                  borderRadius: '0.375rem',
                  '&:hover': {
                    borderColor: '#ced4da'
                  }
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#e9ecef',
                  borderRadius: '0.25rem'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#495057'
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6c757d'
                })
              }}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

export const renderSingleSelectField = (field, deps) => {
  const { reg_key } = field;

  switch (reg_key) {
    case "role_id":
      return renderRoleSelect(field, deps);
    case "programmes":
      return renderProgramSelect(field, deps);
    case "program_study_id":
      return renderProgramStudySelect(field, deps);
    case "university_partners_id":
      return renderUniversityPartnerSelect(field, deps);
    case "year_of_graduation":
      return renderYearSelect(field, deps);
    case "experience_levels":
      return renderExperienceSelect(field, deps);
    case "country":
      return renderCountrySelect(field, deps);
    case "language":
      return renderLanguageSelect(field, deps);
    case "industry_id":
      return renderIndustrySelect(field, deps);
    case "number_of_mantees":
      return renderMenteeCountSelect(field, deps);
    case "any_particular_program":
      return renderProgramInterestSelect(field, deps);
    // Board-related fields
    case "years_of_board_experience":
      return renderYearsOfBoardExperienceSelect(field, deps);
    case "accredited_director":
      return renderAccreditedDirectorSelect(field, deps);
    case "course_completed":
      return renderCoursesCompletedSelect(field, deps);
    default:
      return null;
  }
};

export const renderRadioField = (field, deps) => {
  const { control, errors, clearErrors, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  if (reg_key === "employee") {
    return status ? (
      <Col sm={12} className="mb-3">
        <div className="d-flex flex-column">
          <Form.Label>{field.label}</Form.Label>
          <div
            className="d-flex flex-column"
            style={{ marginBottom: "0px" }}
          >
            <span className="d-flex">
              <Controller
                name={reg_key}
                control={control}
                rules={buildValidationRules(validationRule, is_required)}
                render={({ field }) => (
                  <span className="d-flex">
                    <span className="custom_radio ms-0">
                      {placeholder?.split("|")?.[0]} &nbsp;
                      <input
                        type="radio"
                        checked={
                          field.value ===
                          placeholder?.split("|")?.[0]
                        }
                        value={placeholder?.split("|")?.[0]}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          clearErrors(reg_key);
                          if (e.target.value?.toLowerCase() === "yes") {
                            if (setValue) setValue("employee_end_date", "");
                            if (clearErrors) clearErrors("employee_end_date");
                          }
                        }}
                      />
                      <span className="radio_indicator">&nbsp;</span>
                    </span>

                    <span className="custom_radio">
                      {placeholder?.split("|")?.[1]} &nbsp;{" "}
                      <input
                        type="radio"
                        checked={
                          field.value ===
                          placeholder?.split("|")?.[1]
                        }
                        value={placeholder?.split("|")?.[1]}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          clearErrors(reg_key);
                          if (e.target.value?.toLowerCase() === "yes") {
                            if (setValue) setValue("employee_end_date", "");
                            if (clearErrors) clearErrors("employee_end_date");
                          }
                        }}
                      />
                      <span className="radio_indicator">&nbsp;</span>
                    </span>
                  </span>
                )}
              />
            </span>
            {errors?.[reg_key] && (
              <span
                role="alert"
                style={{
                  color: "#dc3545",
                  fontSize: "0.875em",
                  margin: "4px 0px 8px",
                }}
              >
                {errors?.[reg_key]?.message}
              </span>
            )}
          </div>
        </div>
      </Col>
    ) : null;
  }

  if (reg_key === "gender") {
    return status ? (
      <Col md={12} className="mb-3">
        <div className="d-flex flex-column">
          <div className="form-group" style={{ marginBottom: "0px" }}>
            <Form.Label>{field.label}</Form.Label>
          </div>
          <div className="form-group d-flex" style={{ marginBottom: "0px" }}>
            <span className="">
              <Controller
                name={reg_key}
                control={control}
                rules={buildValidationRules(validationRule, is_required)}
                render={({ field }) => (
                  <span>
                    <span className="custom_radio ms-0">
                      {placeholder?.split("|")?.[0]} &nbsp;
                      <input
                        type="radio"
                        value={placeholder?.split("|")?.[0]}
                        checked={
                          field.value ===
                          placeholder?.split("|")?.[0]
                        }
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          clearErrors(reg_key);
                        }}
                      />
                      <span className="radio_indicator">&nbsp;</span>
                    </span>
                    <span className="custom_radio">
                      {placeholder?.split("|")?.[1]} &nbsp;
                      <input
                        type="radio"
                        value={placeholder?.split("|")?.[1]}
                        checked={
                          field.value ===
                          placeholder?.split("|")?.[1]
                        }
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          clearErrors(reg_key);
                        }}
                      />
                      <span className="radio_indicator">&nbsp;</span>
                    </span>
                  </span>
                )}
              />
            </span>
          </div>
        </div>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Col>
    ) : null;
  }
  return null;
};

const getCheckboxLabel = (reg_key, label) => {
  if (
    reg_key === "is_terms" ||
    reg_key === "is_privacy"
  ) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: label,
        }}
      />
    );
  }

  return label;
};


const toggleExperienceValue = (
  currentValue,
  id,
  field,
  setExperience,
  clearErrors,
  reg_key
) => {
  const newValues = currentValue.includes(id)
    ? currentValue.filter(item => item !== id)
    : [...currentValue, id];

  field.onChange(newValues);
  setExperience(newValues);

  if (newValues.length > 0) {
    clearErrors(reg_key);
  }
};

export const renderCheckboxField = (field, deps) => {
  const { register, errors, control, clearErrors, userExperience, setExperience } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  const validationRule = validation;

  // Handle experience_levels as multiple checkboxes
  if (reg_key === "experience_levels") {
    return status ? (
      <Col md="12" className="mb-3">
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          rules={{
            ...buildValidationRules(validationRule, is_required, reg_key)
          }}
          render={({ field }) => {
            const currentValue = Array.isArray(field.value) ? field.value : [];

            return (
              <>
                {userExperience?.map((val) => (
                  <span key={val.id} className="custom_check me-2">
                    {val?.title}
                    <input
                      type="checkbox"
                      checked={currentValue.includes(val.id)}
                      onChange={() =>
                        toggleExperienceValue(
                          currentValue,
                          val.id,
                          field,
                          setExperience,
                          clearErrors,
                          reg_key
                        )
                      }
                    />
                    <span className="check_indicator">&nbsp;</span>
                  </span>
                ))}
                {/* All of above */}
                <span className="custom_check me-2">
                  All of above{" "}
                  <input
                    type="checkbox"
                    checked={
                      userExperience?.length > 0 &&
                      currentValue.length === userExperience?.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allIds = userExperience.map(exp => exp.id);
                        field.onChange(allIds);
                        setExperience(allIds);
                      } else {
                        field.onChange([]);
                        setExperience([]);
                      }
                      clearErrors(reg_key);
                    }}
                  />
                  <span className="check_indicator">&nbsp;</span>
                </span>
              </>
            );
          }}
        />
        <div>
          {errors?.[reg_key] && (
            <span role="alert" style={{
              color: '#dc3545',
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}>
              {errors?.[reg_key]?.message}
            </span>
          )}
        </div>
      </Col>
    ) : null;
  }

  const simpleCheckboxes = [
    "mn_open_to_talk",
    "mn_loking_for_coach",
    "lgbtq_discussion",
    "mf_mental_health",
    "mf_certified_coach",
    "is_terms",
    "is_privacy",
    "my_consent",
    "is_disclaimer",
    "training_video",
  ];

  if (simpleCheckboxes.includes(reg_key)) {
    return status ? (
      <Col md={12} className="mb-3">
        <Form.Group controlId={`formBasicCheckbox-${reg_key}`}>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id={`checkbox-${reg_key}`}
              {...register(reg_key, {
                ...buildValidationRules(validationRule, is_required),
              })}
            />
            <label className="form-check-label" htmlFor={`checkbox-${reg_key}`}>
              {getCheckboxLabel(reg_key, label)}
            </label>
          </div>
          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
                display: "block",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </Form.Group>
      </Col>
    ) : null;
  }
  return null;
};

const validateFromDate = (value, availabilityChecked, to) => {
  let error = null;
  if (availabilityChecked && !value) error = "From date is required";
  else if (to && new Date(value) > new Date(to)) {
    error = "From date must be earlier than To date";
  }
  return error || true;
};

const validateToDate = (value, availabilityChecked, from) => {
  let error = null;
  if (availabilityChecked && !value) error = "To date is required";
  else if (from && new Date(value) < new Date(from)) {
    error = "To date must be later than From date";
  }
  return error || true;
};

const validateDob = (value, is_required) => {
  let error = null;

  if (is_required && !value) {
    error = "Please select a date.";
  } else if (value) {
    const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(value);

    if (isValidDate) {
      const selectedDate = new Date(value);
      const minAllowed = new Date();
      minAllowed.setFullYear(minAllowed.getFullYear() - 10);

      if (selectedDate > minAllowed) {
        error = "You must be at least 10 years old.";
      }
    } else {
      error = "Please enter a valid date format (YYYY-MM-DD)";
    }
  }

  return error || true;
};

export const renderDatePickerField = (field, deps) => {
  const {
    register,
    control,
    errors,
    setValue,
    watch,
    trigger,
    availabilityChecked,
    showDateDiv,
    checkFromDate,
    checkToDate,
    fromDate,
    toDate,
    setShowDateDiv,
    dob,
    setDob,
    clearErrors,
    employee,
  } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  if (!status) return null;

  if (reg_key === "availability") {
    return (
      <React.Fragment>
        {/* Checkbox for availability */}
        <Col md="12" className="">
          <span className="custom_check me-2 mb-2">
            {label}
            <input
              type="checkbox"
              name="availability"
              checked={availabilityChecked}
              {...register("availability")}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  setShowDateDiv("block");
                } else {
                  setShowDateDiv("none");
                }
                setValue("availability", checked);
                trigger(["from_date", "to_date"]);
              }}
            />
            <span className="check_indicator">&nbsp;</span>
          </span>
        </Col>

        {/* From and To Date Fields */}
        <div className="d-flex mb-3 gap-2">
          {/* From Date */}
          <Col md={6} style={{ display: showDateDiv }}>
            <Form.Group controlId="fromDate">
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                name="from_date"
                {...register("from_date", {
                  required: availabilityChecked
                    ? "From date is required when unavailable"
                    : false,
                  validate: (value) => {
                    const to = watch("to_date");
                    return validateFromDate(value, availabilityChecked, to);
                  },
                })}
                value={fromDate || ""}
                onChange={(e) => {
                  checkFromDate(e.target.value);
                  setValue("from_date", e.target.value, {
                    shouldValidate: true,
                  });
                }}
              />
              {errors?.from_date && (
                <label className="customeValidationText text-left text-danger">
                  {errors.from_date.message}
                </label>
              )}
            </Form.Group>
          </Col>

          {/* To Date */}
          <Col md={6} style={{ display: showDateDiv }}>
            <Form.Group controlId="toDate">
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                name="to_date"
                {...register("to_date", {
                  required: availabilityChecked
                    ? "To date is required when unavailable"
                    : false,
                  validate: (value) => {
                    const from = watch("from_date");
                    return validateToDate(value, availabilityChecked, from);
                  },
                })}
                value={toDate || ""}
                onChange={(e) => {
                  checkToDate(e.target.value);
                  setValue("to_date", e.target.value, {
                    shouldValidate: true,
                  });
                }}
              />
              {errors?.to_date && (
                <label className="customeValidationText text-left text-danger">
                  {errors.to_date.message}
                </label>
              )}
            </Form.Group>
          </Col>
        </div>
      </React.Fragment>
    );
  }

  // Handle employee_start_date and employee_end_date
  if (reg_key === "employee_start_date" || reg_key === "employee_end_date") {
    const currentEmployee = (
      employee ||
      (watch ? watch("employee") : "") ||
      (control?._formValues?.employee) ||
      ""
    )?.toLowerCase();

    // If neither Yes nor No is selected, do not show start date or end date
    if (currentEmployee !== "yes" && currentEmployee !== "no") {
      return null;
    }

    // If currently employed (Yes), hide employee_end_date
    if (reg_key === "employee_end_date" && currentEmployee === "yes") {
      return null;
    }

    const isFieldRequired =
      Number(is_required) === 1 ||
      is_required === true ||
      is_required === "true";

    const cleanLabel = (label || "").replaceAll("*", "").trim();
    const displayLabel = isFieldRequired ? `${cleanLabel} *` : cleanLabel;

    const requiredMessage =
      validationRule
        ?.find((v) => v.key === "required")
        ?.message?.replaceAll(/<[^>]*>/g, "")
        ?.replaceAll("*", "")
        ?.trim() || `${cleanLabel || "Date"} is required`;

    const validationRules = {
      required:
        isFieldRequired &&
        (currentEmployee === "yes" || currentEmployee === "no") &&
        !(reg_key === "employee_end_date" && currentEmployee === "yes")
          ? requiredMessage
          : false,
      validate: (value) => {
        if (currentEmployee !== "yes" && currentEmployee !== "no") {
          return true;
        }

        if (reg_key === "employee_end_date" && currentEmployee === "yes") {
          return true;
        }

        if (!value) {
          if (isFieldRequired) return requiredMessage;
          return true;
        }

        if (reg_key === "employee_start_date") {
          if (currentEmployee === "no" && watch) {
            const endDate = watch("employee_end_date");
            if (endDate) {
              const startNorm = normalizeMonthYear(value);
              const endNorm = normalizeMonthYear(endDate);
              if (startNorm && endNorm) {
                const startDate = new Date(`${startNorm}-01T00:00:00`);
                const endDateObj = new Date(`${endNorm}-01T00:00:00`);
                if (startDate > endDateObj) {
                  return "Start date must be earlier than or equal to end date";
                }
              }
            }
          }
        }

        if (reg_key === "employee_end_date") {
          if (watch) {
            const startDate = watch("employee_start_date");
            if (startDate) {
              const startNorm = normalizeMonthYear(startDate);
              const endNorm = normalizeMonthYear(value);
              if (startNorm && endNorm) {
                const startDateObj = new Date(`${startNorm}-01T00:00:00`);
                const endDateObj = new Date(`${endNorm}-01T00:00:00`);
                if (startDateObj > endDateObj) {
                  return "End date must be greater than or equal to start date";
                }
              }
            }
          }
        }

        return true;
      },
    };

    return (
      <Col md={6} className="asAFormField mb-3">
        <Form.Group controlId={reg_key}>
          <Form.Label className="form-label">{displayLabel}</Form.Label>

          <div className="w-100 react-datepicker-wrapper-custom position-relative">
            {control ? (
              <Controller
                name={reg_key}
                control={control}
                defaultValue=""
                rules={validationRules}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DatePicker
                    id={reg_key}
                    name={reg_key}
                    selected={
                      value && normalizeMonthYear(value)
                        ? new Date(`${normalizeMonthYear(value)}-01T00:00:00`)
                        : null
                    }
                    onChange={(date) => {
                      let dateStr = "";
                      if (date && !isNaN(date.getTime())) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, "0");
                        dateStr = `${year}-${month}`;
                      }
                      onChange(dateStr);
                      if (setValue) setValue(reg_key, dateStr, { shouldValidate: true });
                      if (clearErrors) clearErrors(reg_key);
                      if (trigger) {
                        if (reg_key === "employee_start_date") {
                          trigger("employee_end_date");
                        } else if (reg_key === "employee_end_date") {
                          trigger("employee_start_date");
                        }
                      }
                    }}
                    onBlur={onBlur}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    autoComplete="off"
                    onKeyDown={(e) => e.preventDefault()}
                    placeholderText={placeholder || "Select Month and Year"}
                    className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
                  />
                )}
              />
            ) : (
              <DatePicker
                id={reg_key}
                name={reg_key}
                selected={
                  watch && watch(reg_key) && normalizeMonthYear(watch(reg_key))
                    ? new Date(`${normalizeMonthYear(watch(reg_key))}-01T00:00:00`)
                    : null
                }
                onChange={(date) => {
                  let dateStr = "";
                  if (date && !isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    dateStr = `${year}-${month}`;
                  }
                  if (setValue) setValue(reg_key, dateStr, { shouldValidate: true });
                  if (clearErrors) clearErrors(reg_key);
                  if (trigger) {
                    if (reg_key === "employee_start_date") {
                      trigger("employee_end_date");
                    } else if (reg_key === "employee_end_date") {
                      trigger("employee_start_date");
                    }
                  }
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                autoComplete="off"
                onKeyDown={(e) => e.preventDefault()}
                placeholderText={placeholder || "Select Month and Year"}
                className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
              />
            )}
            <FaRegCalendarAlt
              className="position-absolute"
              style={{
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
          </div>

          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
                display: "block",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </Form.Group>
      </Col>
    );
  }

  if (reg_key === "dob") {
    return (
      <Col md={12} className="asAFormField mb-3">
        <Form.Group controlId={reg_key}>
          <label className="form-label">{label}</label>
          {(() => {
            const today = new Date();
            const maxDobDate = new Date(
              today.setFullYear(today.getFullYear() - 10)
            )
              .toISOString()
              .split("T")[0];

            return (
              <Form.Control
                type="date"
                name={reg_key}
                {...register(reg_key, {
                  ...buildValidationRules(validationRule, is_required),
                  validate: (value) => validateDob(value, is_required),
                })}
                value={dob || ""}
                max={maxDobDate}
                onChange={(e) => {
                  const formattedDate = e.target.value;
                  setDob(formattedDate);
                  setValue("dob", formattedDate);
                  clearErrors("dob");
                }}
              />
            );
          })()}
          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </Form.Group>
      </Col>
    );
  }

  // General Date Field
  const isGeneralRequired =
    Number(is_required) === 1 ||
    is_required === true ||
    is_required === "true";

  const cleanGeneralLabel = (label || "").replaceAll("*", "").trim();
  const displayGeneralLabel = isGeneralRequired ? `${cleanGeneralLabel}*` : cleanGeneralLabel;

  const generalRequiredMsg =
    validationRule
      ?.find((v) => v.key === "required")
      ?.message?.replaceAll(/<[^>]*>/g, "")
      ?.replaceAll("*", "")
      ?.trim() || `${cleanGeneralLabel || "Date"} is required`;

  return (
    <Col md={12} className="asAFormField mb-3">
      <Form.Group controlId={reg_key}>
        <Form.Label>{displayGeneralLabel}</Form.Label>

        {control ? (
          <Controller
            name={reg_key}
            control={control}
            defaultValue=""
            rules={{
              ...buildValidationRules(validationRule, is_required),
              required: isGeneralRequired ? generalRequiredMsg : false,
            }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <Form.Control
                type="date"
                id={reg_key}
                ref={ref}
                value={value || ""}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (clearErrors) clearErrors(reg_key);
                }}
                onBlur={onBlur}
              />
            )}
          />
        ) : (
          <Form.Control
            type="date"
            name={reg_key}
            id={reg_key}
            {...register(reg_key, {
              ...buildValidationRules(validationRule, is_required),
              required: isGeneralRequired ? generalRequiredMsg : false,
            })}
            onChange={(e) => {
              if (setValue) setValue(reg_key, e.target.value, { shouldValidate: true });
              if (clearErrors) clearErrors(reg_key);
            }}
          />
        )}

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{ color: "#dc3545", fontSize: "0.875em", marginTop: "4px", display: "block" }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

const validateResume = (value, is_required) => {
  let error = null;
  if (is_required) {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      error = "Resume is required";
    } else if (typeof value !== "string") {
      const file = value[0];
      if (file && file.type !== "application/pdf") {
        error = "Please upload only PDF files.";
      } else if (file && file.size > 1024 * 1024) {
        error = "Please upload only up to 1 MB in size.";
      }
    }
  }
  return error || true;
};

export const renderFileField = (field, deps) => {
  const {
    control,
    errors,
    src,
    alt,
    selectedFile,
    onChangePicture,
    resumeText,
    onChangeResume,
    clearErrors,
  } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  if (reg_key === "image") {
    return status ? (
      <Col md={12} className="asAFormField mb-3">
        <div className="userprofile text-center py-3">
          <div className="customeValidationImageText text-center form-label">
            Image size should be less than 30 MB*
          </div>
          <span>
            <img
              className="img-fluid rounded-circle userprofile-img"
              src={src}
              alt={alt}
            />
          </span>
          <div>
            <span className="inputfileouter">
              Upload image{" "}
              <Controller
                name={reg_key}
                control={control}
                rules={{
                  ...((selectedFile === "" ||
                    typeof selectedFile !== "string") &&
                    buildValidationRules(validationRule, is_required, reg_key)),
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <input
                    {...field}
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    onChange={(e) => {
                      const files = e.target.files;
                      onChange(files);
                      onChangePicture(e);
                    }}
                    className="form-control"
                  />
                )}
              />
            </span>
          </div>
          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </div>
      </Col>
    ) : null;
  }

  if (reg_key === "resume") {
    return status ? (
      <Col md={12} className="mb-3">
        <Form.Label>{field.label}</Form.Label>
        <div className="form-group inputfileupload studentformdashboard mb-0">
          <input
            type="text"
            disabled
            className="form-control uploadInput"
            value={resumeText || ""}
            placeholder={placeholder}
            readOnly
          />
          <span className="uploadfile uploadfile1" style={{ top: "8px" }}>
            <Controller
              name={reg_key}
              control={control}
              rules={{
                validate: (value) => validateResume(value, is_required),
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <input
                  {...field}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    onChange(e.target.files);
                    onChangeResume(e);
                    clearErrors(reg_key);
                  }}
                  title=""
                />
              )}
            />
            Upload
          </span>
          <sub className="ps-2">Upload only PDF format.</sub>
          <div className="ps-2">
            {errors?.[reg_key] && (
              <span
                role="alert"
                style={{
                  color: "#dc3545",
                  fontSize: "0.875em",
                  margin: "4px 0px 8px",
                  display: "block",
                }}
              >
                {errors?.[reg_key]?.message}
              </span>
            )}
          </div>
        </div>
      </Col>
    ) : null;
  }
  return null;
};

// Helper component for rendering subarea checkbox
const renderSubareaCheckbox = (
  sub,
  areaData,
  areaState,
  formActions,
  areaHandlers
) => {
  const { selectedArea, mainArea } = areaState;
  const { setValue, field, trigger } = formActions;
  const { setMainArea, setSelectedArea } = areaHandlers;

  const isSubSelected = selectedArea.includes(sub.id.toString());

  const handleSubareaChange = (checked) => {
    const subId = sub.id.toString();
    const result = checked
      ? addSubarea(mainArea, selectedArea, areaData, subId)
      : removeSubarea(mainArea, selectedArea, areaData, subId);

    updateFormValues(
      setValue,
      field,
      trigger,
      setMainArea,
      setSelectedArea,
      result.updatedMain,
      result.updatedSubs
    );
  };

  return (
    <li key={sub.id}>
      <input
        type="checkbox"
        id={`subArea${sub.id}`}
        className={`form-check-input mainArea${areaData.id}`}
        value={sub.id}
        checked={isSubSelected}
        onChange={(e) => handleSubareaChange(e.target.checked)}
      />
      <div className="selectboxouter">{sub.name}</div>
    </li>
  );
};

// Helper component for rendering main area with subareas
const renderMainArea = (
  areaData,
  field,
  areaState,
  errors,
  formActions,
  areaHandlers
) => {
  const { mainArea, selectedArea } = areaState;
  const { setValue, trigger } = formActions;
  const { setMainArea, setSelectedArea } = areaHandlers;

  const isMainSelected =
    (field.value || []).includes(areaData.id.toString()) ||
    mainArea.includes(areaData.id.toString());

  const handleMainAreaChange = (checked) => {
    if (checked && mainArea.length >= 3) {
      return;
    }

    const result = checked
      ? addMainAreaWithSubareas(mainArea, selectedArea, areaData)
      : removeMainAreaWithSubareas(mainArea, selectedArea, areaData);

    updateFormValues(
      setValue,
      field,
      trigger,
      setMainArea,
      setSelectedArea,
      result.updatedMain,
      result.updatedSubs
    );
  };

  return (
    <div key={areaData.id}>
      <h5>
        <input
          type="checkbox"
          id={`mainArea${areaData.id}`}
          className={`form-check-input ${errors.mainArea ? "is-invalid" : ""}`}
          value={areaData.id}
          checked={isMainSelected}
          onChange={(e) => handleMainAreaChange(e.target.checked)}
        />
        <div className="selectboxouter">{areaData.name}</div>
      </h5>

      <ul className="bussinessSelect">
        {areaData.subcategory.map((sub) =>
          renderSubareaCheckbox(
            sub,
            areaData,
            areaState,
            { setValue, field, trigger },
            areaHandlers
          )
        )}
      </ul>
    </div>
  );
};

// Helper functions to reduce nesting depth
const getSubareaIds = (subcategory) => {
  return new Set(subcategory.map((s) => s.id.toString()));
};

const addMainAreaWithSubareas = (mainArea, selectedArea, areaData) => {
  const updatedMain = [...mainArea, areaData.id.toString()];
  const subIds = getSubareaIds(areaData.subcategory);
  const updatedSubs = Array.from(new Set([...selectedArea, ...subIds]));
  return { updatedMain, updatedSubs };
};

const removeMainAreaWithSubareas = (mainArea, selectedArea, areaData) => {
  const updatedMain = mainArea.filter((id) => id !== areaData.id.toString());
  const subIds = getSubareaIds(areaData.subcategory);
  const updatedSubs = selectedArea.filter((id) => !subIds.has(id));
  return { updatedMain, updatedSubs };
};

const addSubarea = (mainArea, selectedArea, areaData, subId) => {
  const updatedSubs = [...selectedArea, subId];
  const updatedMain = mainArea.includes(areaData.id.toString())
    ? mainArea
    : [...mainArea, areaData.id.toString()];
  return { updatedMain, updatedSubs };
};

const removeSubarea = (mainArea, selectedArea, areaData, subId) => {
  const updatedSubs = selectedArea.filter((id) => id !== subId);
  const stillHasSubareas = areaData.subcategory.some((s) =>
    updatedSubs.includes(s.id.toString())
  );
  const updatedMain = stillHasSubareas
    ? mainArea
    : mainArea.filter((id) => id !== areaData.id.toString());
  return { updatedMain, updatedSubs };
};

const updateFormValues = (
  setValue,
  field,
  trigger,
  setMainArea,
  setSelectedArea,
  updatedMain,
  updatedSubs
) => {
  setMainArea(updatedMain);
  setSelectedArea(updatedSubs);
  setValue("mainArea", updatedMain);
  setValue("subarea", updatedSubs);
  setValue("domains", updatedMain);
  field.onChange(updatedMain);
  trigger("mainArea");
};

// Helper functions for objectives
const handleObjectiveChange = (
  checked,
  itemValue,
  selectedObjective,
  setSelectedObjective,
  setValue,
  field,
  trigger
) => {
  if (checked && selectedObjective.length >= 3) {
    return;
  }

  const updatedObjectives = checked
    ? [...selectedObjective, itemValue.toString()]
    : selectedObjective.filter((val) => val !== itemValue.toString());

  setSelectedObjective(updatedObjectives);
  setValue("objective", updatedObjectives);
  field.onChange(updatedObjectives);
  trigger("objective");
};

// ✅ Helper function (move outside component or above return)
const renderObjectiveItem = (item, deps) => {
  const {
    selectedObjective,
    errors,
    handleObjectiveChange,
    setSelectedObjective,
    setValue,
    field,
    trigger,
  } = deps;
  const isChecked = selectedObjective.includes(item.value.toString());

  const handleChange = (checked) => {
    handleObjectiveChange(
      checked,
      item.value,
      selectedObjective,
      setSelectedObjective,
      setValue,
      field,
      trigger
    );
  };

  return (
    <li key={item.value}>
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) => handleChange(e.target.checked)}
        className={`form-check-input ${errors?.objective ? "is-invalid" : ""
          }`}
      />
      <div className="selectboxouter">{item.label}</div>
    </li>
  );
};

export const renderCustomSelectField = (field, deps) => {
  const {
    control,
    errors,
    setValue,
    trigger,
    mainArea,
    setMainArea,
    selectedArea,
    setSelectedArea,
    areas,
    selectedObjective,
    setSelectedObjective,
    objective,
    roleId,
    setting,
    formState,
  } = deps;

  const { reg_key, label, validation, status, is_required } = field;

  if (!status) return null;

  const validationRule = validation;

  const isMentor = roleId === "Mentor";

  const getDomainText = () =>
    isMentor ? setting?.mentor_domain_text : setting?.mentees_domain_text;

  const getObjectiveText = () =>
    isMentor ? setting?.mentor_objective_text : setting?.mentees_objective_text;

  const commonValidation = (value, type) => {
    if (!value || value.length === 0) {
      return `Please select at least one ${type}.`;
    }
    if (value.length > 3) {
      return `You can select a maximum of 3 ${type}.`;
    }
    return undefined;
  };

  const renderMap = {
    board_entity_type: () => renderBoardEntityTypeSelect(field, deps),
    board_entity_type_2: () => renderBoardEntityTypeSelect(field, deps),
    course_completed: () => renderCoursesCompletedCustomSelect(field, deps),
    accredited_director: () => renderAccreditedDirectorCustomSelect(field, deps),
    indicate_board_experience_level: () =>
      renderBoardExperienceLevelSelect(field, deps),

    domains: () => (
      <div
        className="profilescreen mb-3 my-3"
        style={{ width: "100%" }}
        ref={(el) => {
          if (el) {
            const hasError = !!errors?.mainArea;
            const submitCount = formState?.submitCount || 0;
            const lastSubmitCount = parseInt(el.dataset.lastSubmitCount || "0", 10);

            const isNewError = hasError && !el.dataset.hadError;
            const isNewSubmitAttempt = hasError && submitCount > lastSubmitCount;

            if (isNewError || isNewSubmitAttempt) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            if (hasError) {
              el.dataset.hadError = "true";
            } else {
              delete el.dataset.hadError;
            }
            el.dataset.lastSubmitCount = submitCount.toString();
          }
        }}
      >
        <h4>Please select up to 3 areas of interest</h4>
        <p className="mt-2">{getDomainText()}</p>

        <div className="businessboxouter">
          <Controller
            name="mainArea"
            control={control}
            rules={{
              ...buildValidationRules(validationRule, is_required),
              validate: (value) => commonValidation(value, "main area"),
            }}
            render={({ field }) => (
              <>
                {errors?.mainArea && (
                  <span className="text-danger d-block mb-2">
                    {errors.mainArea.message}
                  </span>
                )}

                {areas?.map((areaData) =>
                  renderMainArea(
                    areaData,
                    field,
                    { selectedArea, mainArea },
                    errors,
                    { setValue, field, trigger },
                    { setMainArea, setSelectedArea }
                  )
                )}
              </>
            )}
          />
        </div>
      </div>
    ),

    objective: () => (
      <div className="profilescreen mb-3 my-3">
        <h4>{getObjectiveText()}</h4>

        <div className="businessboxouter">
          <label className="form-label">{label}</label>

          <ul className="bussinessSelect">
            <Controller
              name="objective"
              control={control}
              rules={{
                ...buildValidationRules(validationRule, is_required),
                validate: (value) => commonValidation(value, "objective"),
              }}
              render={({ field }) => (
                <>
                  {errors?.objective && (
                    <span className="text-danger d-block mb-2">
                      {errors.objective.message}
                    </span>
                  )}

                  {objective?.map((item) =>
                    renderObjectiveItem(item, {
                      selectedObjective,
                      errors,
                      handleObjectiveChange,
                      setSelectedObjective,
                      setValue,
                      field,
                      trigger,
                    })
                  )}
                </>
              )}
            />
          </ul>
        </div>
      </div>
    ),
  };

  return renderMap[reg_key]?.() || null;
};

export const renderTextareaField = (field, deps) => {
  const { register, errors, watch, countWords } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <div className="form-group">
        <Form.Control
          as="textarea"
          rows="5"
          name={reg_key}
          placeholder={placeholder}
          className={"form-control"}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            validate: {
              whiteSpace: (value) =>
                !value ||
                  (typeof value === "string" && value.trim().length > 0)
                  ? true
                  : "White spaces not allowed.",
            },
          })}
        />
        <p className="mt-2">
          Characters count: {countWords(watch(reg_key) || "")}/1500
        </p>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </div>
    </Col>
  ) : null;
};

// Board Roles Repeater Component - handles dynamic groups of board role entries
const BoardRolesRepeater = ({ allFields, deps }) => {
  const { watch, setValue, trigger, register, setting, formState, errors, clearErrors } = deps;

  // Get the individual field definitions
  const pastBoardRolesField = allFields?.find(f => f.reg_key === 'past_board_roles');
  const repLabelField = allFields?.find(f => f.reg_key === 'rep_label');
  const companyNameField = allFields?.find(f => f.reg_key === 'rep_company_name');
  const boardTitleField = allFields?.find(f => f.reg_key === 'rep_board_title');
  const startDateField = allFields?.find(f => f.reg_key === 'rep_start_date');
  const endDateField = allFields?.find(f => f.reg_key === 'rep_end_date');

  // Parse board_roles_validation from Redux settings
  // Extract min and max values, ignoring the type field
  // Example: [{"key":"min","type":"Mentor","value":1,"message":"At least 2 board roles required"},{"key":"max","type":"Mentees","value":3}]
  // We only care about key, value, and message - ignore type completely
  let minRoles = 0;
  let maxRoles = 6;
  let minValidationMessage = "";

  if (setting?.board_roles_validation) {
    try {
      // Parse JSON string if needed
      const validationData = typeof setting.board_roles_validation === 'string'
        ? JSON.parse(setting.board_roles_validation)
        : setting.board_roles_validation;

      // Find min and max values - ignore the type field completely
      if (Array.isArray(validationData)) {
        // Find the min entry (ignore type)
        const minObj = validationData.find(v => v.key === 'min');
        // Find the max entry (ignore type)
        const maxObj = validationData.find(v => v.key === 'max');

        minRoles = minObj?.value || 0;
        maxRoles = maxObj?.value || 6;
        minValidationMessage = minObj?.message || "";
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  // Prioritize past_board_roles_count from settings if available
  if (
    setting?.past_board_roles_count !== undefined &&
    setting?.past_board_roles_count !== null &&
    setting?.past_board_roles_count !== ""
  ) {
    const parsedCount = parseInt(setting.past_board_roles_count, 10);
    if (!isNaN(parsedCount) && parsedCount > 0) {
      maxRoles = parsedCount;
    }
  } else if (maxRoles < 6) {
    // Ensure limit is at least 6 when past_board_roles_count is not provided
    maxRoles = 6;
  }

  // Check if ALL board role fields have status = 1 (enabled)
  // If any field has status = 0, don't show the entire board roles section
  const allFieldsEnabled = companyNameField?.status === 1 &&
    boardTitleField?.status === 1 &&
    startDateField?.status === 1 &&
    endDateField?.status === 1;

  // Show the repeater ONLY if:
  // 1. All 4 fields exist (fields are defined in registration settings)
  // 2. All 4 fields have status=1 (fields are enabled)
  // 3. maxRoles > 0 (board roles are allowed based on validation settings)
  const shouldShowRepeater = companyNameField && boardTitleField && startDateField && endDateField && allFieldsEnabled && maxRoles > 0;

  // Watch the board_roles array to track current count
  let boardRolesArray = watch('board_roles') || [];

  // Initialize minimum required groups IMMEDIATELY if needed
  // Don't wait for useEffect - do it synchronously on first render
  if (shouldShowRepeater && boardRolesArray.length === 0 && minRoles > 0) {
    const initialRoles = Array.from({ length: minRoles }, () => ({
      rep_company_name: "",
      rep_board_title: "",
      rep_start_date: "",
      rep_end_date: "",
    }));
    setValue('board_roles', initialRoles, { shouldValidate: false });
    boardRolesArray = initialRoles; // Update local variable immediately
  }

  // Register board_roles field with validation
  const checkRequired = (field, value) => {
    if (field?.is_required === 1 && (!value || value.trim() === "")) {
      const requiredValidation = field?.validation?.find(v => v.key === "required");
      return requiredValidation?.message || `${field?.label} is required`;
    }
    return null;
  };

  const validateDate = (role) => {
    if (!role?.is_currently_working && role.rep_start_date && role.rep_end_date) {
      const startDate = new Date(role.rep_start_date);
      const endDate = new Date(role.rep_end_date);

      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }
    return null;
  };

  const validateRole = (role) => {
    const error =
      checkRequired(companyNameField, role.rep_company_name) ||
      checkRequired(boardTitleField, role.rep_board_title) ||
      checkRequired(startDateField, role.rep_start_date);

    if (error) return error;

    if (!role?.is_currently_working) {
      const endDateError = checkRequired(endDateField, role.rep_end_date);
      if (endDateError) return endDateError;
    }

    return validateDate(role);
  };

  // If any of these fields don't exist, don't render
  if (!companyNameField || !boardTitleField || !startDateField) {
    return null;
  }

  // Check if user can add more roles (current count < max)
  const canAddMore = boardRolesArray.length < maxRoles;

  // Add a new empty board role group
  const handleAddRole = () => {
    if (canAddMore) {
      const newRole = {
        rep_company_name: "",
        rep_board_title: "",
        rep_start_date: "",
        rep_end_date: "",
      };
      const updatedRoles = [...boardRolesArray, newRole];
      setValue('board_roles', updatedRoles);
      // Clear board_roles error when user adds a role
      if (clearErrors) clearErrors('board_roles');
    }
  };

  // Remove a specific board role group by index
  const handleRemoveRole = (index) => {
    const updatedRoles = boardRolesArray.filter((_, i) => i !== index);
    setValue('board_roles', updatedRoles);
  };

  // Update a specific field within a board role group
  const handleFieldChange = (index, fieldName, value) => {
    const updatedRoles = [...boardRolesArray];
    updatedRoles[index] = {
      ...updatedRoles[index],
      [fieldName]: value,
    };

    // Special handling for "Currently Working" checkbox - user choice, multiple can be checked
    if (fieldName === 'is_currently_working') {
      if (value === true) {
        // Clear the end date for the currently working role
        updatedRoles[index].rep_end_date = "";
      }
    }

    // Special handling for end date field - NO AUTO-CHECK
    // Don't auto-check "Currently Working" when end date is cleared
    // User must explicitly check the checkbox

    setValue('board_roles', updatedRoles);
    // Trigger validation after change to update errors
    trigger('board_roles');
  };

  // Validate individual field based on field definition from API
  const validateField = (index, fieldName, fieldDef) => {
    const role = boardRolesArray[index];
    const value = role?.[fieldName];

    const requiredError = checkRequiredValidation(value, fieldDef);
    if (requiredError) return requiredError;

    const validationError = checkFieldValidations(value, fieldDef);
    if (validationError) return validationError;

    return null;
  };

  const checkRequiredValidation = (value, fieldDef) => {
    if (fieldDef?.is_required !== 1) return null;

    if (!value || value.trim() === "") {
      const requiredValidation = fieldDef?.validation?.find(
        (v) => v.key === "required"
      );
      return requiredValidation?.message || `${fieldDef?.label} is required`;
    }

    return null;
  };

  const checkFieldValidations = (value, fieldDef) => {
    // ✅ VERY IMPORTANT FIX
    if (!value || value.trim() === "") return null;

    if (!fieldDef?.validation) return null;

    for (const validation of fieldDef.validation) {
      const error =
        validateMin(value, validation) ||
        validateMax(value, validation) ||
        validatePattern(value, validation);

      if (error) return error;
    }

    return null;
  };

  const validateMin = (value, validation) => {
    if (validation.key === "minlength" || validation.key === "min") {
      if (value.length < validation.length) {
        return validation.message;
      }
    }
    return null;
  };

  const validateMax = (value, validation) => {
    if (validation.key === "maxlength" || validation.key === "max") {
      if (value.length > validation.length) {
        return validation.message;
      }
    }
    return null;
  };

  const validatePattern = (value, validation) => {
    if (validation.key === "pattern" && validation.regex) {
      const regex = new RegExp(validation.regex.slice(1, -1));
      if (!regex.test(value)) {
        return validation.message;
      }
    }
    return null;
  };

  // Validate all board roles before submission
  // Validate date range: rep_start_date must be <= rep_end_date
  // Start Date is required, End Date is optional (unless not currently working)
  const validateDateRange = (index) => {
    const role = boardRolesArray[index];

    // If currently working, end date is not required and validation is skipped
    if (role?.is_currently_working) {
      return null;
    }

    // If not currently working, validate date range
    if (role?.rep_start_date && role?.rep_end_date) {
      const startDate = new Date(role.rep_start_date);
      const endDate = new Date(role.rep_end_date);

      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }

    // If only end date is provided without start date, that's an error
    if (!role?.rep_start_date && role?.rep_end_date) {
      return "Start date is required when end date is provided";
    }

    return null;
  };

  if (!shouldShowRepeater) {
    return null;
  }

  // Show field-level errors after submit attempt (formState.isSubmitted) OR when errors exist
  const showValidationErrors = formState?.isSubmitted || !!errors?.board_roles;
  const isBoardRoleRequired =
    companyNameField?.is_required === 1 ||
    boardTitleField?.is_required === 1 ||
    startDateField?.is_required === 1 ||
    endDateField?.is_required === 1;
  const hasEmptyRole = boardRolesArray.every(role =>
    !role.rep_company_name &&
    !role.rep_board_title &&
    !role.rep_start_date &&
    !role.rep_end_date
  );

  return (
    <>
      {/* Render each board role group inline */}
      {boardRolesArray.map((role, index) => {

        // Validation errors — show after submit attempt
        const companyError = showValidationErrors
          ? validateField(index, "rep_company_name", companyNameField)
          : null;

        const titleError = showValidationErrors
          ? validateField(index, "rep_board_title", boardTitleField)
          : null;

        const startDateError = showValidationErrors
          ? validateField(index, "rep_start_date", startDateField)
          : null;

        const dateRangeError = showValidationErrors
          ? validateDateRange(index)
          : null;

        // End date validation logic
        const isEndDateRequired =
          showValidationErrors &&
          !role?.is_currently_working &&
          endDateField?.is_required === 1;

        const isEndDateEmpty =
          !role?.rep_end_date || role?.rep_end_date === "";

        const showEndDateError = isEndDateRequired && isEndDateEmpty;

        const endDateErrorMessage =
          endDateField?.validation?.find(v => v.key === "required")?.message ||
          `${endDateField?.label} is required`;



        return (
          <Col md={12} key={`board_role_${index}`} className="mb-4">
            {index === 0 && (
              <>
                <Form.Label>{pastBoardRolesField?.label}</Form.Label>
                {/* Disclaimer */}
                {repLabelField?.status === 1 && repLabelField?.label && (
                  <div className="mx-0 mb-2" style={{ fontSize: "0.875em", color: "#6c757d" }}
                    dangerouslySetInnerHTML={{ __html: repLabelField.label }}
                  />
                )}
                {/* Show top-level board_roles error set by onSubmit */}
                {errors?.board_roles && (
                  <div style={{ color: "#dc3545", fontSize: "0.875em", marginBottom: "8px" }}>
                    {errors.board_roles.message}
                  </div>
                )}
                {showValidationErrors && isBoardRoleRequired && hasEmptyRole && !errors?.board_roles && (
                  <div style={{ color: "#dc3545", fontSize: "0.875em" }}>
                    Add at least one board role
                  </div>
                )}
              </>
            )}
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                padding: "20px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <div className="row">

                {/* Company Name */}
                <Col md={12} className="mb-3">
                  <Form.Label className="form-label">
                    {companyNameField?.label} {companyNameField?.is_required ? "*" : ""}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id={`rep_company_name_${index}`}
                    name={`rep_company_name_${index}`}
                    placeholder={companyNameField?.placeholder}
                    maxLength={255}
                    value={role?.rep_company_name || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "rep_company_name", e.target.value)
                    }
                    className={companyError ? "is-invalid" : ""}
                  />
                  {companyError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {companyError}
                    </span>
                  )}
                </Col>

                {/* Board Title */}
                <Col md={12} className="mb-3">
                  <Form.Label className="form-label">
                    {boardTitleField?.label} {boardTitleField?.is_required ? "*" : ""}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id={`rep_board_title_${index}`}
                    name={`rep_board_title_${index}`}
                    placeholder={boardTitleField?.placeholder}
                    maxLength={255}
                    value={role?.rep_board_title || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "rep_board_title", e.target.value)
                    }
                    className={titleError ? "is-invalid" : ""}
                  />
                  {titleError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {titleError}
                    </span>
                  )}
                </Col>

                {/* Start Date */}
                <Col md={6} className="mb-3">
                  <Form.Label className="form-label">
                    {startDateField?.label} {startDateField?.is_required ? "*" : ""}
                  </Form.Label>
                  <div className="w-100 react-datepicker-wrapper-custom position-relative">
                    <DatePicker
                      id={`rep_start_date_${index}`}
                      name={`rep_start_date_${index}`}
                      selected={
                        role?.rep_start_date
                          ? new Date(
                              `${normalizeMonthYear(role.rep_start_date)}-01T00:00:00`
                            )
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          handleFieldChange(index, "rep_start_date", `${year}-${month}`);
                        } else {
                          handleFieldChange(index, "rep_start_date", "");
                        }
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      autoComplete="off"
                      onKeyDown={(e) => e.preventDefault()}
                      placeholderText={startDateField?.placeholder || "Select Month and Year"}
                      className={`form-control ${startDateError ? "is-invalid" : ""}`}
                    />
                    <FaRegCalendarAlt
                      className="position-absolute"
                      style={{
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                  {startDateError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {startDateError}
                    </span>
                  )}
                </Col>

                {/* End Date */}
                {!role?.is_currently_working && (
                  <Col md={6} className="mb-3">
                    <Form.Label className="form-label">
                      {endDateField?.label} {endDateField?.is_required ? "*" : ""}
                    </Form.Label>

                    <div className="w-100 react-datepicker-wrapper-custom position-relative">
                      <DatePicker
                        id={`rep_end_date_${index}`}
                        name={`rep_end_date_${index}`}
                        selected={
                          role?.rep_end_date
                            ? new Date(
                                `${normalizeMonthYear(role.rep_end_date)}-01T00:00:00`
                              )
                            : null
                        }
                        onChange={(date) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            handleFieldChange(index, "rep_end_date", `${year}-${month}`);
                          } else {
                            handleFieldChange(index, "rep_end_date", "");
                          }
                        }}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        autoComplete="off"
                        onKeyDown={(e) => e.preventDefault()}
                        placeholderText={endDateField?.placeholder || "Select Month and Year"}
                        className={`form-control ${showEndDateError ? "is-invalid" : ""}`}
                        disabled={role?.is_currently_working}
                      />
                      <FaRegCalendarAlt
                        className="position-absolute"
                        style={{
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      />
                    </div>

                    {showEndDateError && (
                      <span
                        role="alert"
                        style={{
                          color: "#dc3545",
                          fontSize: "0.875em",
                          margin: "4px 0px 8px",
                          display: "block",
                        }}
                      >
                        {endDateErrorMessage}
                      </span>
                    )}

                    {dateRangeError && (
                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "0.875em",
                          margin: "4px 0px 8px",
                          display: "block",
                        }}
                      >
                        {dateRangeError}
                      </span>
                    )}
                  </Col>
                )}

                {/* Checkbox + Remove */}
                <Col md={12}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Form.Check
                      type="checkbox"
                      id={`currently_working_${index}`}
                      label="Currently in this role"
                      checked={role?.is_currently_working || false}
                      onChange={(e) =>
                        handleFieldChange(index, "is_currently_working", e.target.checked)
                      }
                    />

                    {boardRolesArray.length > minRoles && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger d-flex align-items-center justify-content-center"
                        onClick={() => handleRemoveRole(index)}
                        title="Remove this board role"
                        style={{ width: "32px", height: "32px", padding: 0 }}
                      >
                        <FaMinus size={12} />
                      </button>
                    )}
                  </div>
                </Col>

              </div>
            </div>
          </Col>
        );
      })}

      {/* Minimum count validation message - only show if form was submitted */}
      {showValidationErrors && minRoles > 0 && boardRolesArray.length > 0 && boardRolesArray.length < minRoles && (
        <Col md={12} className="mb-3">
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
            }}
          >
            {minValidationMessage || `At least ${minRoles} board role(s) required`}
          </span>
        </Col>
      )}

      {/* Add More Button - shown only if max not reached, placed after last end_date */}
      {/* Show add button if: current count < max AND we have at least min groups */}
      {canAddMore && boardRolesArray.length >= minRoles && (
        <Col md={12} className="mb-3">
          <button
            type="button"
            className="btn loginsubmit btn-red"
            onClick={handleAddRole}
            title={`Add another board role (${boardRolesArray.length}/${maxRoles})`}
          >
            + Add Board Role
          </button>
        </Col>
      )}
    </>
  );
};

BoardRolesRepeater.propTypes = {
  allFields: PropTypes.shape({
    find: PropTypes.func
  }).isRequired,
  deps: PropTypes.shape({
    errors: PropTypes.shape({
      board_roles: PropTypes.string
    }).isRequired,
    watch: PropTypes.func,
    setValue: PropTypes.func,
    trigger: PropTypes.func,
    register: PropTypes.string,
    setting: PropTypes.shape({
      board_roles_validation: PropTypes.string
    }).isRequired,
    formState: PropTypes.shape({
      isSubmitted: PropTypes.string
    }).isRequired,

  }).isRequired
}

// Export wrapper that properly instantiates the React component
// This ensures useEffect hooks run correctly
export const renderBoardRolesInline = (allFields, deps) => {
  return <BoardRolesRepeater allFields={allFields} deps={deps} />;
};

export const renderRegisterField = (field, deps) => {
  const { field_type } = field;

  switch (field_type) {
    case "text":
      return renderTextField(field, deps);
    case "email":
      return renderEmailField(field, deps);
    case "password":
      return renderPasswordField(field, deps);
    case "SingleSelect":
      return renderSingleSelectField(field, deps);
    case "radio":
      return renderRadioField(field, deps);
    case "checkbox":
      return renderCheckboxField(field, deps);
    case "DatePicker":
      return renderDatePickerField(field, deps);
    case "file":
      return renderFileField(field, deps);
    case "CustomSelect":
      return renderCustomSelectField(field, deps);
    case "textarea":
      return renderTextareaField(field, deps);
    default: {
      // Handle unknown field types as text labels (like disclaimer)
      const { label, status } = field;
      if (field?.reg_key == "past_board_roles") {
        return null;
      }
      else {
        return status ? (
          <div className="mx-3 mt-2 mb-3">{label}</div>
        ) : null;
      }
    }
  }
};
